import { BlockStack, Card, InlineGrid, Text } from '@shopify/polaris'
import type { BccData } from './useBccData'

// ── Helpers ───────────────────────────────────────────────────────────────────

function egp(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000)     return `${(n / 1_000).toFixed(0)}K`
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 })
}

function pct(n: number | null | undefined): string {
  if (n == null) return '—'
  return `${n.toFixed(1)}%`
}

// ── Sub-component ─────────────────────────────────────────────────────────────

type MetricProps = {
  label:   string
  value:   string
  sub?:    string
  tone?:   'success' | 'critical' | 'caution'
}

function Metric({ label, value, sub, tone }: MetricProps) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text as="p" variant="bodySm" tone="subdued">{label}</Text>
        <Text as="p" variant="headingXl" tone={tone}>{value}</Text>
        {sub && <Text as="p" variant="bodySm" tone="subdued">{sub}</Text>}
      </BlockStack>
    </Card>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

type Props = { data: BccData }

export function ExecutiveStrip({ data }: Props) {
  const { dashboard, cash, forecast } = data

  const targetPct = forecast.targetProgress?.revenuePct ?? null

  const projectedPct = forecast.targetProgress
    ? (forecast.projected.projectedRevenue / forecast.targetProgress.revenueTarget) * 100
    : null

  // confidence.score is a 0–1 fraction (modules/metrics/confidence.ts)
  const confidenceScore = Math.round(cash.confidence.score * 100)
  const confidenceTone: MetricProps['tone'] =
    confidenceScore >= 80 ? 'success' : confidenceScore >= 60 ? 'caution' : 'critical'

  const targetTone: MetricProps['tone'] =
    targetPct == null ? undefined :
    targetPct >= 90   ? 'success' :
    targetPct >= 70   ? 'caution' : 'critical'

  const daysLeft = forecast.pacing.workingDaysLeft

  return (
    <InlineGrid columns={5} gap="300">
      {/* profit.net subtracts only order-driven costs, so its margin is an
          operating margin. The true net sits in Financial Position, below
          fixed expenses. See §9.4. */}
      <Metric
        label="Operating Profit"
        value={`EGP ${egp(dashboard.profit.net)}`}
        sub={`${pct(dashboard.profit.netMargin)} operating margin`}
        tone={dashboard.profit.net >= 0 ? 'success' : 'critical'}
      />
      <Metric
        label="Collected (COD)"
        value={`EGP ${egp(cash.collectedThisMonth)}`}
        sub="In your account this month"
      />
      <Metric
        label="Target Progress"
        value={pct(targetPct)}
        sub={targetPct != null ? `${daysLeft} working days left` : 'No target set'}
        tone={targetTone}
      />
      <Metric
        label="Month-end Forecast"
        value={pct(projectedPct)}
        sub={`EGP ${egp(forecast.projected.projectedRevenue)} projected`}
        tone={projectedPct != null && projectedPct >= 90 ? 'success' : 'caution'}
      />
      <Metric
        label="Data Confidence"
        value={`${confidenceScore}%`}
        sub={cash.confidence.level}
        tone={confidenceTone}
      />
    </InlineGrid>
  )
}
